import { CmnfncBaseViewModel, CmnfncBaseInitialState } from './features/cmnfnc-base/cmnfnc-base-view-model'
import {StorageUtil, SessionStorageUtil, AuthorizationService} from '@ar2/common'
/**
 * アプリケーション表示モデル
 * @author nabla2.metamodel.generator
 */
export class ViewModel {
  cmnfnc_base: CmnfncBaseViewModel
  errorMessages: {modelPath:string, message:string}[]
  modalErrorMessages: {modelPath:string, message:string}[]
  codeDefinitions: {[category:string]:{label:string,value:string}[]}
}

/**
 * アプリケーションステータス管理用ユーティリティ
 */
export namespace ViewModel {

  /**
   * アプリケーション初期状態
   */
  const InitialState:ViewModel = {
    cmnfnc_base: CmnfncBaseInitialState,
    errorMessages: [],
    modalErrorMessages: [],
    codeDefinitions: {},
  }

  /**
   * アプリケーション初期状態を取得する
   */
  export const getInitialState = ():ViewModel => {
    return JSON.parse(JSON.stringify(InitialState))
  }

  /**
   * アプリケーション初期状態の指定されたパス上の値を取得する
   * @param path パス
   */
  export const getInitialValue = (path:string):any => {
    const state = get(path, InitialState)
    return JSON.parse(JSON.stringify(state))
  }

  /**
   * 指定されたモデルの指定されたパス上の値を取得する
   *
   * @param path パス
   * @param model モデル
   * @param nullSafeValue 対応するパス上に値が存在しない場合に返される値。デフォルトではnullが返される。
   */
  export const get = (path:string, model:ViewModel, nullSafeValue:any=null):any => {
    if (!path) return model
    if (model == null) return nullSafeValue
    const segments = path.split('.')
    let node:any = model
    while (segments.length){
      const key = segments.shift()
      node = node[key]
      if (node == null) return nullSafeValue
    }
    return node
  }

  /**
   * 指定されたモデルの指定されたパス上の値が更新されたモデルを返す。
   * 更新後のモデルは別インスタンス(変更部分以外はシャローコピー)となる。
   * @param path パス
   * @param diff 更新内容
   * @param current モデル
   */
  export const update = (path:string, diff:any, current:ViewModel, isMerge:boolean=false):ViewModel => {
    if (!path) throw new Error(
      'path must not be blank: ' + path
    )
    if (isMerge && typeof diff === 'undefined') {
      return current
    }
    const segments = path.split('.')
    let root:any = {...current}
    let node:any = root
    while (segments.length){
      const key = segments.shift()
      if (!segments.length) {
        if (!isMerge || !Array.isArray(diff) && (typeof(diff) !== 'object')) {
          node[key] = diff
        }
        else if (Array.isArray(diff)) {
          node[key] = [...(node[key]||[]), ...(diff||[])]
        }
        else if (typeof diff === 'object') {
          node[key] = {...(node[key]||{}), ...(diff||{})}
        }
      }
      else if (!node.hasOwnProperty(key)) {
        node[key] = (!segments.length || !looksInt(segments[0])) ? {} : []
      }
      node = node[key]
    }
    return root
  }

  export const merge = (path:string, diff:any, current:ViewModel):ViewModel => {
    return update(path, diff, current, true);
  }

  const looksInt = (obj:any):boolean => {
    const type = (typeof obj)
    return (type === 'number' || type === 'string') && /^[\d]+$/.test(obj + '')
  }

  export const mergeUpdates = (diffs:{[path:string]: any}, current:ViewModel):ViewModel => {
    return updates(diffs, current, true)
  }

  /**
   * 複数のパス上の値を一括更新する。
   * 更新後のモデルは別インスタンス(変更部分以外はシャローコピー)となる。
   * @param diffs 変更パスと変更値にからなるマップ
   * @param current モデル
   */
  export const updates = (diffs:{[path:string]: any}, current:ViewModel, isMerge:boolean=false):ViewModel => {
    return Object.keys(diffs).reduce((updated, path) => {
      const diff = diffs[path]
      return ViewModel.update(path, diff, updated, isMerge)
    }, current)
  }

  /**
   * 指定されたパス上のデータ断面を取得する。取得された断面はディープコピーとなる。
   * @param path 断面を取得するパス
   * @param model 対象モデル
   * @param auth 認可サービス
   * @param persistent 取得した断面をローカルストレージに保持するかどうか(デフォルト:true)
   * @param toSessionStorage 取得した断面をローカルストレージではなくセッションストレージに保持するかどうか(デフォルト:false)
   */
  export const takeSnapshot = (path:string, model:ViewModel, auth:AuthorizationService, persistent:boolean=true, toSessionStorage:boolean=false):void => {
    let blngCd = auth.getSelectedGbuCD();
    let prefix = blngCd && "_" + blngCd + "_"
    const snapshot = get(path, model)
    if (persistent) {
      if (toSessionStorage) {
        SessionStorageUtil.SET(path, snapshot)
      } else {
        StorageUtil.SET(prefix + path, snapshot)
      }
    }
    else {
      SNAPSHOT_HEAP[prefix + path] = JSON.parse(JSON.stringify(snapshot))
    }
  }

  /**
   * 指定されたパス上の直近のデータ断面に状態を戻す。
   * データ断面が存在しない場合は、アプリケーション初期状態に戻す。
   * @param path 対象のパス
   * @param current 対象モデル
   * @param auth 認可サービス
   * @param fromSessionStorage ローカルストレージではなくセッションストレージから断面を取得するかどうか(デフォルト:false)
   */
  export const revertToLatestSnapshot = (path:string, current:ViewModel, auth:AuthorizationService, fromSessionStorage:boolean=false):ViewModel => {
    return update(path, getSnapshotOf(path, auth, fromSessionStorage, true), current)
  }

  /**
   * 指定されたパス上の直近のデータ断面に状態を戻す。
   * データ断面が存在しない場合は、アプリケーション初期状態を返す。
   * @param path 対象のパス
   * @param auth 認可サービス
   * @param fromSessionStorage ローカルストレージではなくセッションストレージから断面を取得するかどうか(デフォルト:false)
   * @param deepCopy スナップショットの値をディープコピーするかどうか(デフォルト:false)
   */
  export const getSnapshotOf = (path:string, auth:AuthorizationService, fromSessionStorage:boolean=false, deepCopy:boolean=false):any => {
    let blngCd = auth.getSelectedGbuCD();
    let prefix = blngCd && "_" + blngCd + "_"
    if (SNAPSHOT_HEAP.hasOwnProperty(prefix + path)) {
      return !deepCopy ? SNAPSHOT_HEAP[prefix + path] : JSON.parse(JSON.stringify(SNAPSHOT_HEAP[prefix + path]));
    }
    const snapshot = fromSessionStorage ? SessionStorageUtil.GET(path) : StorageUtil.GET(prefix + path)
    return (typeof snapshot === 'undefined')
         ? getInitialValue(path)
         : snapshot
  }

  const SNAPSHOT_HEAP:{[path:string]:any} = {}

  /**
   * 指定されたパス上の直近のデータ断面と現在の状態との間に際があるかどうかを返す。
   * @param path 対象のパス
   * @param current 現在のモデル
   * @param auth 認可サービス
   * @param fromSessionStorage ローカルストレージではなくセッションストレージから断面を取得するかどうか(デフォルト:false)
   */
  export const updatedFromLatestSnapshot = (path:string, current:ViewModel, auth:AuthorizationService, fromSessionStorage:boolean=false):boolean => {
    let curJson: string = JSON.stringify(get(path, current))
    // remove '_showFlg' property added by fixation table component
    curJson = curJson.replace(/,?"(_showFlg|_changed)":(true|false)/g, '')
    return curJson != JSON.stringify(getSnapshotOf(path, auth, fromSessionStorage))
  }
}


// WEBPACK FOOTER //
// ./src/view-model.ts